import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { sortSensors } from 'commons/src/commonFunctions';
import { SensorIcon } from 'commons/src/components/sensors/SensorUnit';
import { sensorUnits } from 'commons/src/constants';
import Error from '../../components/error';
import { publicDashboardPlaceholder } from '../../components/placeholder';
import { CurrentSensorValuesType, DeviceSensorDetailsType } from '../../models/commons';
import { Store } from '../../reducers';

interface StateProps {
    deviceDataLoading: boolean;
    deviceDataError: boolean;
    sensorDataLoading: boolean;
    sensorDataError: boolean;
    sensorData: CurrentSensorValuesType[];
    sensorDetails?: { [sensor: string]: DeviceSensorDetailsType };
}

type Props = StateProps;

export const YugeViewComponent = ({
    deviceDataLoading,
    deviceDataError,
    sensorDataLoading,
    sensorDataError,
    sensorData,
    sensorDetails,
}: Props) => {
    const { t: txt } = useTranslation();
    const [selectedSensors, setSelectedSensors] = useState<string[]>([]);
    const [search] = useSearchParams();

    useEffect(() => {
        const footerElement = document.getElementById('footer');
        if (footerElement) footerElement.style.display = 'none';
    }, []);

    useEffect(() => {
        const searchQuery = search.toString();
        if (searchQuery) {
            const sensors = searchQuery
                .slice(searchQuery.indexOf('?') + 1)
                .split('&')
                .map(sensor => sensor.replace('=', ''));
            setSelectedSensors(sensors);
        }
    }, [search]);

    const isLoading = deviceDataLoading || sensorDataLoading;
    const error = deviceDataError || sensorDataError;
    if (error) {
        return (
            <Error
                errorHeader="PublicIAQDashboard.DeviceNotPublic"
                errorText="ErrorCodes.PUBLIC_DEVICE_NOT_FOUND"
                errorIcon="visibility_off"
            />
        );
    }

    const sensorsInDevice = sensorData.map(sensor => sensor.type);
    const sensorsInDeviceNotRequested = sensorsInDevice.filter(sensor => !selectedSensors.includes(sensor));
    const sensorOrder = [...selectedSensors, ...sensorsInDeviceNotRequested];
    const tileBody = sortSensors(sensorData, sensorOrder, 2).map((sensor, index) => {
        const sensorDetail = sensorDetails && sensorDetails[sensor.type];
        const unit = sensorDetail && sensorUnits[sensorDetail.unit];
        const displayUnit =
            sensorDetail && (sensorDetail.unit === 'pct' || sensorDetail.unit === 'c' || sensorDetail.unit === 'f');
        return (
            <div key={`sensor-${sensor.type}-${index}`} className="yuge-view__sensor">
                <div className="yuge-view__sensor__group">
                    <div className="yuge-view__sensor__value">
                        {sensor.value}
                        {displayUnit && unit}
                    </div>
                    <div className="flex yuge-view__sensor__type">
                        <SensorIcon sensor={sensor.type} />
                        <div>{txt(sensor.type)}</div>
                    </div>
                </div>
            </div>
        );
    });

    return (
        <ReactPlaceholder ready={!isLoading} customPlaceholder={publicDashboardPlaceholder}>
            <div className="yuge-view">{tileBody}</div>
        </ReactPlaceholder>
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        publicDeviceData: { loading: deviceDataLoading, error: deviceDataError, sensorDetails },
        publicDeviceSensorData: { loading: sensorDataLoading, error: sensorDataError, sensorData },
    } = state;
    return {
        deviceDataLoading,
        deviceDataError,
        sensorDataLoading,
        sensorDataError,
        sensorData,
        sensorDetails,
    };
};

export default connect(mapStateToProps)(YugeViewComponent);
